import { FC, useCallback, useState } from 'react';
import { css } from '@bts-web/utils-style-engine';
import { submitButtonWrapperAndButtonProperStyles } from '../../utils/styles';
import { SubmitButtonWrapper } from '../../Reusable/SubmitButtonWrapper/SubmitButtonWrapper';
import { PoweredBy } from '../../Reusable/PoweredBy/PoweredBy';
import { HyperLinkButtonBase } from '../../../common';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { FilledSpinnerIos } from '../../../common/svgIcons';

interface VerificationLinkButtonProps {
  isDataLoading: boolean;
  verificationLink: string;
  poweredByText?: string;
  verifyButtonText: string;
  verifyTradeInfoText?: string;
}

const VerificationLinkButton: FC<VerificationLinkButtonProps> = ({
  isDataLoading,
  verificationLink,
  poweredByText,
  verifyButtonText,
  verifyTradeInfoText,
}) => {
  const { t } = useClientTranslation();

  const [isButtonDisabledAfterClick, setIsButtonDisabledAfterClick] =
    useState(false);

  const handleClick = useCallback(() => {
    setIsButtonDisabledAfterClick(true);

    // Re-enable after 2 seconds automatically, in case user doesn't leave the page
    // (to avoid the button being disabled forever if user doesn't navigate away)
    setTimeout(() => {
      setIsButtonDisabledAfterClick(false);
    }, 2000);
  }, []);

  const isLoading = isDataLoading || isButtonDisabledAfterClick;

  return (
    <SubmitButtonWrapper>
      <PoweredBy
        centered
        value={poweredByText || t('powered_by_@tenant')}
        showLogo={!poweredByText}
      />

      {verifyTradeInfoText && (
        <p
          className={css({
            color: 'neutrals.text_primary',
            fontSize: 'body.medium',
            fontWeight: 'body.medium',
            letterSpacing: 'body.medium',
            lineHeight: 'body.medium',
            textAlign: 'start',
            mb: 'extra_small',
          })}
          data-testid="verifyTradeInfo"
          aria-label="verify trade info"
        >
          {verifyTradeInfoText}
        </p>
      )}

      <div className={submitButtonWrapperAndButtonProperStyles}>
        <HyperLinkButtonBase
          to={verificationLink}
          useHtmlAnchor
          fullWidth
          visual="primary"
          disabled={isLoading}
          onClick={handleClick}
        >
          {verifyButtonText}

          {isLoading && (
            <FilledSpinnerIos
              aria-label="loading icon"
              data-element="loading-icon"
              style={{ marginInlineStart: 8 }}
            />
          )}
        </HyperLinkButtonBase>
      </div>
    </SubmitButtonWrapper>
  );
};

export { VerificationLinkButton };
